import { KeyboardArrowDown } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import Markdown from '@app/src/components/Markdown'
import RequestItem from '@app/src/pages/Questionnaire/Provider/Report/RequestItem'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import { AnswerStatus, RequestSection } from '@app/src/types/resourceExplorer'
import { useIntl } from 'react-intl'

type SectionProps = {
  section: RequestSection
  sectionNumber: number
}

const Section: React.FC<SectionProps> = ({ section, sectionNumber }) => {
  const { response } = useReport()
  const { formatMessage } = useIntl()
  const { control, getValues } = useFormContext()

  const sectionItems = useMemo(() => {
    return section.items.map(item => item.id)
  }, [section.items])

  const watchedItemValues = useWatch({
    control,
    name: sectionItems.map(itemId => `${itemId}`),
    defaultValue: sectionItems.map(itemId => getValues(`${itemId}`)),
  })

  const areAllItemsExcluded = useMemo(() => {
    if (!section.items.length) return false

    if (!watchedItemValues || typeof watchedItemValues !== 'object') return false

    return section.items.every(item => {
      const itemValue = watchedItemValues[item.id]
      return itemValue?.answerStatus === AnswerStatus.Excluded
    })
  }, [watchedItemValues, section.items])

  const sectionContent = (
    <>
      {!areAllItemsExcluded && (
        <Box display="flex" flexDirection="column" mt={sectionNumber > 1 ? 7 : 0}>
          <Typography variant="overline" gutterBottom>
            {formatMessage({ id: 'questionnaire.section' }, { number: sectionNumber })}
          </Typography>
          <Typography variant="h2" gutterBottom>
            <Markdown>{section.title}</Markdown>
          </Typography>
          <Typography variant="body1">
            <Markdown>{section.description}</Markdown>
          </Typography>
        </Box>
      )}

      {section.items.map((item, index) => {
        const responseItem = response?.items.find(responseItem => responseItem.requestItemId === item.id)
        return (
          <RequestItem
            key={item.id}
            requestItem={item}
            responseItem={responseItem}
            itemNumber={index + 1}
            sectionNumber={sectionNumber}
          />
        )
      })}
    </>
  )

  if (areAllItemsExcluded) {
    return (
      <Accordion
        sx={({ palette }) => ({
          '&.MuiPaper-root': {
            borderRadius: 2,
            bgcolor: palette.grey[100],
            mt: sectionNumber > 1 ? 7 : 0,
          },
        })}
        elevation={0}
        disableGutters
      >
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Stack
            direction="row"
            pt={2}
            pb={2}
            px={2}
            spacing={2}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="overline" color="textSecondary" gutterBottom>
                {formatMessage({ id: 'questionnaire.section' }, { number: sectionNumber })}
              </Typography>
              <Typography variant="h3" color="textSecondary" gutterBottom>
                <Markdown>{section.title}</Markdown>
              </Typography>
            </Box>
            <Typography ml={2} variant="subtitle1" color="textSecondary">
              {formatMessage({ id: 'templateBuilder.questionRules.excluded' })}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography ml={2}>
            {formatMessage({ id: 'templateBuilder.questionRules.excludedFallback' }, { isSection: true })}
          </Typography>
          {sectionContent}
        </AccordionDetails>
      </Accordion>
    )
  }

  return sectionContent
}

export default Section
